import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, FileText, Code2 } from 'lucide-react';
import { toast } from 'react-toastify';
import automacoesService from '../services/automacoesService';

export default function CreateCampaignModal({ open, onClose, pastaId, onCreated }) {
  const [nome, setNome] = useState('');
  const [tab, setTab] = useState('blank');
  const [mode, setMode] = useState('blank');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!open) return null;

  const reset = () => {
    setNome('');
    setTab('blank');
    setMode('blank');
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleCreate = async () => {
    if (!nome || submitting) return;
    setSubmitting(true);
    try {
      const r = await automacoesService.criar({ nome, pasta_id: pastaId || null });
      onCreated?.();
      handleClose();
      navigate(`/automacoes/${r.data.id}/editar`);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao criar campanha');
      setSubmitting(false);
    }
  };

  const canCreate = !!nome && mode === 'blank' && !submitting;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[480px] rounded-2xl bg-base border border-white/[0.06] shadow-2xl"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.04]">
          <div className="text-base font-semibold">Nova campanha</div>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-surface-1 text-ink-4 hover:text-ink-2"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-ink-3 mb-1.5 font-medium">Nome</label>
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Boas-vindas Curso Analista"
              className="w-full bg-surface-1 border border-white/[0.06] focus:border-emerald-500/40 rounded-lg px-3 py-2 text-sm outline-none"
            />
          </div>

          <div className="flex gap-1 bg-surface-1 p-1 rounded-lg border border-white/[0.06]">
            <button
              onClick={() => setTab('blank')}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm transition-colors ${tab === 'blank' ? 'bg-surface-2 text-ink-1' : 'text-ink-4 hover:text-ink-2'}`}
            >
              Criar campanha
            </button>
            <button
              onClick={() => setTab('tpl')}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm transition-colors ${tab === 'tpl' ? 'bg-surface-2 text-ink-1' : 'text-ink-4 hover:text-ink-2'}`}
            >
              Templates
            </button>
          </div>

          {tab === 'blank' && (
            <div className="space-y-2">
              <label
                className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer ${mode === 'blank' ? 'bg-emerald-500/5 border border-emerald-500/30' : 'bg-surface-1 border border-white/[0.06] hover:border-white/[0.08]'}`}
              >
                <input
                  type="radio"
                  name="mode"
                  checked={mode === 'blank'}
                  onChange={() => setMode('blank')}
                  className="accent-emerald-500 mt-0.5"
                />
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-ink-1">
                    <FileText className="w-4 h-4 text-emerald-400" />
                    Campanha em branco
                  </div>
                  <div className="text-xs text-ink-4 mt-0.5">
                    Crie uma campanha nova, sem nenhuma predefinição
                  </div>
                </div>
              </label>

              <label
                className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer ${mode === 'code' ? 'bg-brand-500/5 border border-brand-500/30' : 'bg-surface-1 border border-white/[0.06] hover:border-white/[0.08]'}`}
              >
                <input
                  type="radio"
                  name="mode"
                  checked={mode === 'code'}
                  onChange={() => setMode('code')}
                  className="accent-emerald-500 mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm font-medium text-ink-1">
                    <Code2 className="w-4 h-4 text-brand-400" />
                    Via código
                    <span className="text-[10px] uppercase tracking-wider text-ink-4 font-semibold">
                      (em breve)
                    </span>
                  </div>
                  <div className="text-xs text-ink-4 mt-0.5">
                    Clone a estrutura de outra campanha colando o código
                  </div>
                  {mode === 'code' && (
                    <textarea
                      rows={4}
                      placeholder='{"nodes":[],"edges":[]}'
                      disabled
                      className="mt-2 w-full font-mono text-xs bg-base border border-white/[0.06] rounded-md px-2 py-1.5 outline-none resize-none opacity-60 cursor-not-allowed"
                    />
                  )}
                </div>
              </label>
            </div>
          )}

          {tab === 'tpl' && (
            <div className="p-6 text-center text-sm text-ink-4 border border-dashed border-white/[0.06] rounded-lg">
              Galeria de templates aparece aqui
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-white/[0.04]">
          <button
            onClick={handleClose}
            className="px-3 py-1.5 rounded-lg text-sm text-ink-3 hover:bg-surface-1"
          >
            Cancelar
          </button>
          <button
            onClick={handleCreate}
            disabled={!canCreate}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold ${canCreate ? 'bg-emerald-500 hover:bg-emerald-600 text-emerald-950' : 'bg-surface-2 text-ink-5 cursor-not-allowed'}`}
          >
            Criar fluxo
          </button>
        </div>
      </div>
    </div>
  );
}
